import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { useCustomersUiStore, type CustomerLayoutMode } from '@/store/customersUiStore';
import { isCompanyAdmin, isCashierLevel } from '@/types';
import { posApi } from '@/services/api';
import { isLocalFirstMode, listLocalCustomersWithStats } from '@/offline';
import { useSyncStore } from '@/store/syncStore';
import { getApiErrorMessage } from '@/utils/supplierValidation';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import { Users, Plus, Search, Menu, FileText, Upload, Activity, Package, Tag } from 'lucide-react';
import PakRupeeIcon from '@/components/common/PakRupeeIcon';
import ContactCard from '@/components/common/ContactCard';
import CustomerListRow from '@/components/customers/CustomerListRow';
import type { POSCustomer } from '@/types';
import Pagination, { LIST_PAGE_SIZE } from '@/components/common/Pagination';
import { CustomerLedgerModal } from '@/components/CustomerLedgerModal';
import PartyLedgerModal from '@/components/PartyLedgerModal';
import SaleInvoiceLedgerModal from '@/components/customers/SaleInvoiceLedgerModal';
import AddCustomerPaymentModal from '@/components/customers/AddCustomerPaymentModal';
import CustomerFormModal from '@/components/customers/CustomerFormModal';
import EntityImportWizard from '@/components/dataImport/EntityImportWizard';
import { customerImportConfig } from '@/components/dataImport/entities/customerImportConfig';
import ConfirmModal from '@/components/common/ConfirmModal';
import toast from 'react-hot-toast';

export default function CustomersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role } = useAuthStore();
  const isAdmin = isCompanyAdmin(role);
  const { toggleSidebar } = useUIStore();
  const layoutMode = useCustomersUiStore((s) => s.layoutMode);
  const setLayoutMode = useCustomersUiStore((s) => s.setLayoutMode);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [showLedger, setShowLedger] = useState(false);
  const [showPartyLedger, setShowPartyLedger] = useState(false);
  const [showInvoiceLedger, setShowInvoiceLedger] = useState(false);
  const [ledgerCustomer, setLedgerCustomer] = useState<string>('');
  const [ledgerCustomerName, setLedgerCustomerName] = useState<string>('');
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [selectedCustomerObj, setSelectedCustomerObj] = useState<any>(null);
  const [editingCustomer, setEditingCustomer] = useState<POSCustomer | null>(null);
  const [editModalTab, setEditModalTab] = useState<'basic' | 'prices'>('basic');
  const [deleteConfirm, setDeleteConfirm] = useState<{
    id: string;
    label: string;
    type: 'customer' | 'supplier' | 'product' | 'variant';
    errorMessage?: string;
  } | null>(null);
  const qc = useQueryClient();

  useEffect(() => {
    setPage(0);
  }, [search]);

  const posSyncMode = useSyncStore((s) => s.posSyncMode);
  const catalogSyncedAt = useSyncStore((s) => s.catalogSyncedAt);
  const pendingQueueCount = useSyncStore((s) => s.pendingQueueCount);
  const failedQueueCount = useSyncStore((s) => s.failedQueueCount);

  const { data: customersData, isLoading } = useQuery({
    queryKey: ['customers', search, page, posSyncMode, catalogSyncedAt, pendingQueueCount, failedQueueCount],
    queryFn: async () => {
      if (isLocalFirstMode()) {
        const all = await listLocalCustomersWithStats(search || undefined);
        const start = page * LIST_PAGE_SIZE;
        return {
          customers: all.slice(start, start + LIST_PAGE_SIZE) as unknown as POSCustomer[],
          total: all.length,
        };
      }
      const res = await posApi.getCustomers({
        search,
        start: page * LIST_PAGE_SIZE,
        limit: LIST_PAGE_SIZE,
      });
      const message = res.data.message;
      return {
        customers: message.customers as POSCustomer[],
        total: (message.total as number) || 0,
      };
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (name: string) => posApi.deleteCustomer(name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['posCustomers'] });
      qc.invalidateQueries({ queryKey: ['posCustomersSearch'] });
      qc.invalidateQueries({ queryKey: ['posCustomerSelect'] });
      toast.success(t('customers.deletedSuccess', 'Customer deleted successfully'));
      setDeleteConfirm(null);
    },
    onError: (err: any) => {
      const errMsg = getApiErrorMessage(err, 'Failed to delete customer');
      setDeleteConfirm((prev) => prev ? { ...prev, errorMessage: errMsg } : null);
    },
  });

  const handleDeleteCustomer = (id: string, label: string) => {
    setDeleteConfirm({ id, label, type: 'customer' });
  };

  const openEdit = (customer: POSCustomer, tab: 'basic' | 'prices' = 'basic') => {
    setEditingCustomer(customer);
    setEditModalTab(tab);
    setShowModal(true);
  };

  const handleExportData = async () => {
    import('react-hot-toast').then(({ default: toast }) => {
      const toastId = toast.loading('Preparing backup data...');
      posApi.exportCustomersData()
        .then(res => {
          const blob = new Blob([res.data], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          
          const contentDisposition = res.headers['content-disposition'];
          let filename = 'Customers_Export.csv';
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^"]+)"?/);
            if (match && match[1]) {
              filename = match[1];
            }
          }
          
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          toast.success('Backup downloaded successfully', { id: toastId });
        })
        .catch(err => {
          console.error(err);
          toast.error('Failed to download backup', { id: toastId });
        });
    });
  };

  const customers = customersData?.customers || [];
  const customersTotal = customersData?.total || 0;

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <TrialBanner />
        <div className="p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <button onClick={toggleSidebar} className="lg:hidden p-2 -ml-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg shrink-0">
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('customers.title')}</h1>
                <p className="text-gray-500 text-sm">{t('customers.subtitle')}</p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setShowLedger(true)} className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-50 flex items-center gap-2 transition-colors shadow-sm">
                <FileText className="w-4 h-4" /> {t('customers.saleRegister')}
              </button>
              <button
                type="button"
                onClick={() => setShowImportWizard(true)}
                className="btn-secondary flex items-center gap-2"
              >
                <Upload className="w-4 h-4" /> {t('common.import')}
              </button>
              {isAdmin && (
                <button
                  type="button"
                  onClick={handleExportData}
                  className="btn-secondary flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" /> Backup Data
                </button>
              )}
              <button onClick={() => { setEditingCustomer(null); setEditModalTab('basic'); setShowModal(true); }} className="btn-primary flex items-center gap-2">
                <Plus className="w-4 h-4" /> {t('customers.add')}
              </button>
            </div>
          </div>

          {/* Search + layout switch */}
          <div className="card p-4 mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="relative flex-1 max-w-xl">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('customers.searchPlaceholder')}
                  className="input-field pl-9"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {([
                  { mode: 'grid' as CustomerLayoutMode, label: t('customers.layoutGrid') },
                  { mode: 'list' as CustomerLayoutMode, label: t('customers.layoutList') },
                ]).map(({ mode, label }) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setLayoutMode(mode)}
                    className={`px-2.5 py-2 rounded-lg text-xs font-medium border transition-colors ${
                      layoutMode === mode
                        ? 'bg-primary-50 border-primary-300 text-primary-800'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Customers Grid / List */}
          {isLoading ? (
            <div className="card p-8 text-center text-gray-400">{t('customers.loading')}</div>
          ) : customers.length === 0 ? (
            <div className="card p-8 text-center text-gray-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{t('customers.empty')}</p>
            </div>
          ) : (
            <>
              {layoutMode === 'list' ? (
                <div className="flex flex-col gap-2">
                  {customers.map((customer) => {
                    const totalSale = customer.total_sale || 0;
                    const outstanding = customer.outstanding_balance || 0;
                    const received = customer.paid_amount || 0;
                    const progressPercentage = totalSale ? Math.round((received / totalSale) * 100) : 0;
                    const sharedProps = {
                      name: customer.is_walk_in ? `${customer.customer_name} ${t('customers.walkIn')}` : customer.customer_name,
                      subtitle: customer.customer_type || 'Retailer',
                      isDefault: Boolean(customer.is_default),
                      phone: customer.mobile,
                      postalCode: customer.postal_code,
                      address: [customer.address, customer.city].filter(Boolean).join(', '),
                      onClick: () => navigate(`/customers/${encodeURIComponent(customer.name)}`),
                      onEdit: !isCashierLevel(role) ? () => openEdit(customer) : undefined,
                      onDelete: isAdmin && !customer.is_walk_in && !customer.is_default
                        ? () => handleDeleteCustomer(customer.name, customer.customer_name)
                        : undefined,
                      onLedger: () => {
                        setLedgerCustomer(customer.name);
                        setLedgerCustomerName(customer.customer_name);
                        setShowPartyLedger(true);
                      },
                      stats: [
                        {
                          value: totalSale,
                          label: t('customers.totalSales'),
                          icon: Package,
                          iconColorClass: 'text-lime-600',
                          bgClass: 'bg-lime-50',
                        },
                        {
                          value: received,
                          label: t('customers.received'),
                          icon: PakRupeeIcon,
                          iconColorClass: 'text-gray-400',
                          bgClass: 'bg-gray-50',
                        },
                        {
                          value: outstanding,
                          label: t('customers.outstanding'),
                          icon: PakRupeeIcon,
                          iconColorClass: 'text-red-600',
                          bgClass: 'bg-red-50',
                        },
                      ],
                      progressLabel: t('customers.receivedVsOutstanding'),
                      progressPercentage,
                      progressSuffix: t('customers.received'),
                      actions: [
                        {
                          label: 'Register',
                          icon: FileText,
                          onClick: () => { setLedgerCustomer(customer.name); setShowLedger(true); },
                          textClass: 'text-primary-700',
                          bgClass: 'bg-primary-50',
                          hoverClass: 'hover:bg-primary-100',
                        },
                        {
                          label: 'Payment',
                          icon: Activity,
                          onClick: () => {
                            setSelectedCustomerObj({
                              name: customer.name,
                              customer_name: customer.customer_name,
                              outstanding_balance: outstanding,
                            });
                            setShowAddPayment(true);
                          },
                          textClass: 'text-indigo-700',
                          bgClass: 'bg-indigo-50',
                          hoverClass: 'hover:bg-indigo-100',
                        },
                        {
                          label: t('common.invoices'),
                          icon: FileText,
                          onClick: () => {
                            setLedgerCustomer(customer.name);
                            setLedgerCustomerName(customer.customer_name);
                            setShowInvoiceLedger(true);
                          },
                          textClass: 'text-amber-700',
                          bgClass: 'bg-amber-50',
                          hoverClass: 'hover:bg-amber-100',
                        },
                        {
                          label: 'Quotations',
                          icon: FileText,
                          onClick: () => {
                            const query = customer.customer_name || customer.name || '';
                            navigate(`/sales?tab=quotes&search=${encodeURIComponent(query)}&status=All`);
                          },
                          textClass: 'text-indigo-700',
                          bgClass: 'bg-indigo-50',
                          hoverClass: 'hover:bg-indigo-100',
                        },
                        {
                          label: t('customers.specialPrices', 'Special Prices'),
                          icon: Tag,
                          onClick: () => openEdit(customer, 'prices'),
                          textClass: 'text-emerald-700',
                          bgClass: 'bg-emerald-50',
                          hoverClass: 'hover:bg-emerald-100',
                        },
                      ],
                    };
                    return <CustomerListRow key={customer.name} {...sharedProps} />;
                  })}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-stretch">
                  {customers.map((customer) => {
                    const totalSale = customer.total_sale || 0;
                    const outstanding = customer.outstanding_balance || 0;
                    const received = customer.paid_amount || 0;
                    const progressPercentage = totalSale ? Math.round((received / totalSale) * 100) : 0;

                    return (
                      <ContactCard
                        key={customer.name}
                        name={customer.is_walk_in ? `${customer.customer_name} ${t('customers.walkIn')}` : customer.customer_name}
                        subtitle={customer.customer_type || 'Retailer'}
                        onClick={() => navigate(`/customers/${encodeURIComponent(customer.name)}`)}
                        onEdit={!isCashierLevel(role) ? () => openEdit(customer) : undefined}
                        onDelete={isAdmin && !customer.is_walk_in && !customer.is_default ? () => handleDeleteCustomer(customer.name, customer.customer_name) : undefined}
                        onLedger={() => {
                          setLedgerCustomer(customer.name);
                          setLedgerCustomerName(customer.customer_name);
                          setShowPartyLedger(true);
                        }}
                        isDefault={Boolean(customer.is_default)}
                        stats={[
                          {
                            value: totalSale,
                            label: t('customers.totalSales'),
                            icon: Package,
                            iconColorClass: 'text-lime-600',
                            bgClass: 'bg-lime-50'
                          },
                          {
                            value: received,
                            label: t('customers.received'),
                            icon: PakRupeeIcon,
                            iconColorClass: 'text-gray-400',
                            bgClass: 'bg-gray-50'
                          },
                          {
                            value: outstanding,
                            label: t('customers.outstanding'),
                            icon: PakRupeeIcon,
                            iconColorClass: 'text-red-600',
                            bgClass: 'bg-red-50'
                          }
                        ]}
                        progressLabel={t('customers.receivedVsOutstanding')}
                        progressPercentage={progressPercentage}
                        progressSuffix={t('customers.received')}
                        phone={customer.mobile}
                        address={[customer.address, customer.postal_code, customer.city].filter(Boolean).join(', ')}
                        actions={[
                          {
                            label: 'Register',
                            icon: FileText,
                            onClick: () => { setLedgerCustomer(customer.name); setShowLedger(true); },
                            textClass: 'text-primary-700',
                            bgClass: 'bg-primary-50',
                            hoverClass: 'hover:bg-primary-100'
                          },
                          {
                            label: 'Payment',
                            icon: Activity,
                            onClick: () => {
                              setSelectedCustomerObj({
                                name: customer.name,
                                customer_name: customer.customer_name,
                                outstanding_balance: outstanding
                              });
                              setShowAddPayment(true);
                            },
                            textClass: 'text-indigo-700',
                            bgClass: 'bg-indigo-50',
                            hoverClass: 'hover:bg-indigo-100'
                          },
                          {
                            label: t('common.invoices'),
                            icon: FileText,
                            onClick: () => {
                              setLedgerCustomer(customer.name);
                              setLedgerCustomerName(customer.customer_name);
                              setShowInvoiceLedger(true);
                            },
                            textClass: 'text-amber-700',
                            bgClass: 'bg-amber-50',
                            hoverClass: 'hover:bg-amber-100'
                          },
                          {
                            label: 'Quotations',
                            icon: FileText,
                            onClick: () => {
                              const query = customer.customer_name || customer.name || '';
                              navigate(`/sales?tab=quotes&search=${encodeURIComponent(query)}&status=All`);
                            },
                            textClass: 'text-indigo-700',
                            bgClass: 'bg-indigo-50',
                            hoverClass: 'hover:bg-indigo-100'
                          },
                          {
                            label: t('customers.specialPrices', 'Special Prices'),
                            icon: Tag,
                            onClick: () => openEdit(customer, 'prices'),
                            textClass: 'text-emerald-700',
                            bgClass: 'bg-emerald-50',
                            hoverClass: 'hover:bg-emerald-100'
                          }
                        ]}
                      />
                    );
                  })}
                </div>
              )}
              <div className="card overflow-hidden mt-4">
                <Pagination
                  page={page}
                  pageSize={LIST_PAGE_SIZE}
                  total={customersTotal}
                  onPageChange={setPage}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <CustomerFormModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingCustomer(null);
          setEditModalTab('basic');
        }}
        customer={editingCustomer}
        initialTab={editModalTab}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['customers'] });
          qc.invalidateQueries({ queryKey: ['posCustomers'] });
          qc.invalidateQueries({ queryKey: ['posCustomersSearch'] });
          qc.invalidateQueries({ queryKey: ['posCustomerSelect'] });
        }}
      />
      <CustomerLedgerModal
        isOpen={showLedger}
        onClose={() => {
          setShowLedger(false);
          setLedgerCustomer('');
          qc.invalidateQueries({ queryKey: ['customers'] });
        }}
        customers={customersData?.customers || []}
        initialCustomer={ledgerCustomer}
      />
      <PartyLedgerModal
        isOpen={showPartyLedger}
        onClose={() => {
          setShowPartyLedger(false);
          setLedgerCustomer('');
          setLedgerCustomerName('');
          qc.invalidateQueries({ queryKey: ['customers'] });
        }}
        partyType="POS Customer"
        party={ledgerCustomer}
        partyName={ledgerCustomerName}
      />
      <SaleInvoiceLedgerModal
        isOpen={showInvoiceLedger}
        onClose={() => {
          setShowInvoiceLedger(false);
          setLedgerCustomer('');
          setLedgerCustomerName('');
          qc.invalidateQueries({ queryKey: ['customers'] });
        }}
        customer={ledgerCustomer}
        customerName={ledgerCustomerName}
      />
      <AddCustomerPaymentModal
        isOpen={showAddPayment}
        onClose={() => {
          setShowAddPayment(false);
          qc.invalidateQueries({ queryKey: ['customers'] });
        }}
        customer={selectedCustomerObj}
      />

      {showImportWizard && (
        <EntityImportWizard
          config={customerImportConfig}
          onClose={() => {
            setShowImportWizard(false);
            qc.invalidateQueries({ queryKey: ['customers'] });
          }}
          onSuccess={() => qc.invalidateQueries({ queryKey: ['customers'] })}
        />
      )}

      {deleteConfirm && !deleteConfirm.errorMessage && (
        <ConfirmModal
          title={t('confirmDelete.title', 'Confirm Delete')}
          message={t('confirmDelete.message', {
            label: deleteConfirm.label,
            defaultValue: `Are you sure you want to delete "${deleteConfirm.label}"? This action is permanent and cannot be undone.`,
          })}
          confirmText={t('common.delete', 'Delete')}
          cancelText={t('common.cancel', 'Cancel')}
          isPending={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(deleteConfirm.id)}
          onClose={() => setDeleteConfirm(null)}
          type="danger"
        />
      )}

      {deleteConfirm?.errorMessage && (
        <ConfirmModal
          title={t('confirmDelete.cannotDelete', 'Cannot Delete')}
          message={deleteConfirm.errorMessage}
          confirmText={t('confirmDelete.ok', 'OK')}
          hideCancel
          onConfirm={() => setDeleteConfirm(null)}
          onClose={() => setDeleteConfirm(null)}
          type="warning"
        />
      )}
    </div>
  );
}